const INDEX_URL = '/collections/all?view=search-index';
const MINIMUM_TERM_LENGTH = 2;
const scriptElement = document.querySelector('script[data-metafield-search]');
const previewThemeId = scriptElement?.dataset.previewThemeId;
const themeRole = scriptElement?.dataset.themeRole;

/** @type {Promise<Array<{id: string, title: string, author: string, genre: string}>> | null} */
let indexPromise = null;

function normalize(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('it')
    .trim();
}

async function loadIndexPage(page) {
  const url = new URL(INDEX_URL, window.location.origin);
  url.searchParams.set('page', String(page));
  if (previewThemeId && themeRole !== 'main') {
    url.searchParams.set('preview_theme_id', previewThemeId);
  }

  const response = await fetch(url, { credentials: 'same-origin' });
  if (!response.ok) throw new Error(`Search index request failed: ${response.status}`);
  return response.json();
}

async function loadIndex() {
  if (indexPromise) return indexPromise;

  indexPromise = (async () => {
    const products = [];
    let page = 1;
    let totalPages = 1;

    do {
      const payload = await loadIndexPage(page);
      products.push(...(Array.isArray(payload.products) ? payload.products : []));
      totalPages = Number(payload.pages) || 1;
      page += 1;
    } while (page <= totalPages);

    return products;
  })().catch((error) => {
    indexPromise = null;
    throw error;
  });

  return indexPromise;
}

/**
 * Expands a native Shopify query with product IDs whose existing metafields
 * contain the typed author or genre. No product data is changed.
 * @param {string} term
 * @returns {Promise<string>}
 */
export async function buildMetafieldSearchQuery(term) {
  const originalTerm = String(term || '').trim();
  const normalizedTerm = normalize(originalTerm);
  if (normalizedTerm.length < MINIMUM_TERM_LENGTH) return originalTerm;

  try {
    const products = await loadIndex();
    const matchingTitles = products
      .filter((product) =>
        [product.author, product.genre].some((value) => normalize(value).includes(normalizedTerm))
      )
      .map((product) => String(product.title || '').trim())
      .filter(Boolean)
      .filter((title, index, titles) => titles.indexOf(title) === index);

    if (!matchingTitles.length) return originalTerm;
    return matchingTitles.map((title) => `title:${JSON.stringify(title)}`).join(' OR ');
  } catch (error) {
    console.error('Unable to extend search with author and genre metafields', error);
    return originalTerm;
  }
}

/**
 * Builds the results URL used by Enter in the predictive search.
 * @param {string} term
 * @returns {Promise<string>}
 */
export async function buildMetafieldSearchUrl(term) {
  const url = new URL(Theme.routes.search_url, window.location.origin);
  url.searchParams.set('q', await buildMetafieldSearchQuery(term));
  url.searchParams.set('type', 'product');
  url.searchParams.set('options[prefix]', 'last');
  return url.toString();
}

window.DiffusioneMetafieldSearch = {
  buildQuery: buildMetafieldSearchQuery,
  buildUrl: buildMetafieldSearchUrl,
};

document.addEventListener(
  'submit',
  async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.dataset.metafieldSearchReady === 'true') return;

    const action = new URL(form.action, window.location.origin);
    const queryInput = form.elements.namedItem('q');
    if (!action.pathname.endsWith('/search') || !(queryInput instanceof HTMLInputElement)) return;

    event.preventDefault();
    const formData = new FormData(form);
    formData.set('q', await buildMetafieldSearchQuery(queryInput.value));
    formData.set('type', 'product');
    formData.set('options[prefix]', 'last');

    action.search = new URLSearchParams(formData).toString();
    window.location.assign(action.toString());
  },
  true
);
